// libraries
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import Big from 'big.js'
import { Box, Typography } from '@mui/material'
import { FormProvider, useForm, useFormState } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

// components
import Input from 'components/generic/form/Input'
import Loading from 'components/generic/Loading'
import CreateBulkInvoicePaymentTable from 'components/containers/invoice-payments/components/tables/CreateBulkInvoicePaymentTable'
import QuickAddAdjustments from 'components/containers/invoice-payments/components/misc/QuickAddAdjustments'
import DistributeButton from 'components/containers/invoice-payments/components/misc/DistributeButton'
import QuickAddAdjustmentButton from 'components/containers/invoice-payments/components/misc/QuickAddAdjustmentButton'
import CreateBulkInvoicePaymentForm from 'components/containers/invoice-payments/components/misc/CreateBulkInvoicePaymentForm'

// helpers and utils
import { useCreateInvoiceBulkPaymentMutation } from 'services/invoicePayments'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { dispatchSnackbar, setDecimalPrecision } from 'utils/helpers'
import { ROUNDING_PRECISIONS } from 'utils/constants'
import type { InvoicePaymentCreation } from 'components/containers/invoice-payments/types'
import { safeNumber } from 'components/containers/invoice-payments/utils/utils'

const CreateBulkInvoicePaymentContainer: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const methods = useForm()
  const { control, getValues, setValue, handleSubmit } = methods
  const { errors } = useFormState({ control })

  const [isQuickAddAdjustmentsOpen, setIsQuickAddAdjustmentsOpen] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null)
  const [selectedInvoices, setSelectedInvoices] = useState<InvoicePaymentCreation[]>([])
  const [openInvoices, setOpenInvoices] = useState<InvoicePaymentCreation[]>([])
  const [isLoadingFormData, setIsLoadingFormData] = useState<boolean>(false)

  const [createInvoiceBulkPayment, { isLoading: isLoadingCreateBulkInvoicePayment }] =
    useCreateInvoiceBulkPaymentMutation()

  useGlobalLoader(isLoadingFormData || isLoadingCreateBulkInvoicePayment)

  const handleOpenQuickAddAdjustments = () => {
    setIsQuickAddAdjustmentsOpen(true)
  }

  const handleCloseQuickAddAdjustments = () => {
    setIsQuickAddAdjustmentsOpen(false)
  }

  const onRecordPaymentHandler = async (data: any) => {
    const paymentSplits = selectedInvoices.map(invoice => ({
      targetInvoiceId: invoice.id,
      amount: data.invoices[invoice.id]?.amountPaid || 0,
      adjustment: data.invoices[invoice.id]?.adjustment || 0,
      notes: data.invoices[invoice.id]?.adjustmentNote || '',
    }))

    const formattedData = {
      invoiceBulkPaymentCreation: {
        customerId: selectedCustomerId,
        amountPaid: parseFloat(data.paymentAmount),
        checkNumber: data.checkNumber,
        paymentDate: data.paymentDate,
        paymentType: data.paymentMethod,
        paymentSplits: paymentSplits,
        notes: data.paymentNote || '',
      },
      customerId: selectedCustomerId,
      invoiceIds: paymentSplits.map(split => split.targetInvoiceId),
    }

    await createInvoiceBulkPayment(formattedData)
      .unwrap()
      .then(response => {
        dispatchSnackbar(dispatch, 'success', 'Invoice payment successfully recorded')
        navigate(`/app/sales/invoice-payments/${response.id}`)
      })
      .catch((error: any) => {
        const errorMessage = error?.data?.error || 'There was an error recording the invoice payment'
        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }

  const onAddAdjustments = (invoiceAdjustments: { id: string; adjustment: number }[], adjustmentNote: string) => {
    invoiceAdjustments.forEach(invoice => {
      setValue(`invoices.${invoice.id}.adjustment`, invoice.adjustment)
      setValue(`invoices.${invoice.id}.adjustmentNote`, adjustmentNote)
    })
  }

  /**
   * Handles the selection change of invoices and allocates payments accordingly.
   *
   * @param {string[]} selectedIds - Array of selected invoice ids.
   * @param {boolean} isAllSelected - Flag indicating if all invoices are selected.
   *
   * @description
   * This function performs the following operations:
   * 1. Calculates the current payment amount and initializes remaining amount to allocate.
   * 2. Filters openInvoices to get the new list of selected invoices
   * 3. Resets payment values for all invoices. Removes adjustment for unselected invoices
   * 4. Allocates payments based on whether all invoices are selected or not:
   *    - If all selected: Allocates from top to bottom until the payment amount is exhausted.
   *    - If partially selected: Uses existing logic to allocate payments to selected invoices.
   * 5. Updates the component and form state with new values.
   */
  const handleInvoiceSelectionChange = (selectedIds: string[], isAllSelected: boolean) => {
    const currentPaymentAmount = Big(safeNumber(getValues('paymentAmount')))
    let remainingToAllocate = currentPaymentAmount
    const newInvoicesFormData = { ...getValues('invoices') }

    const dictOfSelectedInvoices = {}
    selectedIds.forEach(selectedInvoiceId => {
      dictOfSelectedInvoices[selectedInvoiceId] = true
    })

    const newSelectedInvoices = openInvoices.filter(invoice => dictOfSelectedInvoices[invoice.id])

    // Reset values for all invoices
    openInvoices.forEach(invoice => {
      newInvoicesFormData[invoice.id] = {
        ...newInvoicesFormData[invoice.id],
        amountPaid: '0.00',
      }
      // clear adjustment field for all invoices that are not selected
      if (!dictOfSelectedInvoices[invoice.id]) {
        newInvoicesFormData[invoice.id].adjustment = 0
      }
    })

    if (isAllSelected) {
      // Allocate payment to all invoices from top to bottom
      newSelectedInvoices.forEach(invoice => {
        if (remainingToAllocate.gt(0)) {
          const adjustmentAmount = Big(safeNumber(newInvoicesFormData[invoice.id]?.adjustment))
          const amountDue = Big(safeNumber(invoice.amountDue)).sub(adjustmentAmount).round(ROUNDING_PRECISIONS.COST)
          const payment = amountDue.lt(remainingToAllocate) ? amountDue : remainingToAllocate
          newInvoicesFormData[invoice.id] = {
            ...newInvoicesFormData[invoice.id],
            amountPaid: setDecimalPrecision(payment.toNumber(), {
              decimalPrecisionOverride: 2,
              shouldFormatNumber: false,
            }),
          }
          remainingToAllocate = remainingToAllocate.minus(payment)
        }
      })
    } else {
      // logic for partial selection
      const allocatePayment = (invoice: InvoicePaymentCreation) => {
        const adjustmentAmount = Big(safeNumber(newInvoicesFormData[invoice.id]?.adjustment))
        const amountDue = Big(safeNumber(invoice.amountDue)).sub(adjustmentAmount).round(ROUNDING_PRECISIONS.COST)
        const currentPayment = Big(safeNumber(newInvoicesFormData[invoice.id]?.amountPaid))

        if (remainingToAllocate.gt(0)) {
          const payment = amountDue.minus(currentPayment).lt(remainingToAllocate)
            ? amountDue.minus(currentPayment)
            : remainingToAllocate
          newInvoicesFormData[invoice.id] = {
            ...newInvoicesFormData[invoice.id],
            amountPaid: setDecimalPrecision(currentPayment.plus(payment).toNumber(), {
              decimalPrecisionOverride: 2,
              shouldFormatNumber: false,
            }),
          }
          remainingToAllocate = remainingToAllocate.minus(payment).round(ROUNDING_PRECISIONS.COST)
        }
      }

      // Distribute to selected invoices
      if (currentPaymentAmount.gt(0)) {
        newSelectedInvoices.forEach(allocatePayment)
      }
    }

    setValue('invoices', newInvoicesFormData)
    setSelectedInvoices(newSelectedInvoices)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onRecordPaymentHandler)} data-testid="payment-form">
        <CreateBulkInvoicePaymentForm
          setOpenInvoices={setOpenInvoices}
          setIsLoadingFormData={setIsLoadingFormData}
          onRecordPaymentHandler={onRecordPaymentHandler}
          selectedCustomerId={selectedCustomerId}
          setSelectedCustomerId={setSelectedCustomerId}
          setSelectedInvoices={setSelectedInvoices}
        />
        <Box p={3} width="95%" data-testid="open-invoices-section">
          <Box display="flex" alignItems="center" mb={2} data-testid="open-invoices-header">
            <Typography variant="h6" mr={4} data-testid="open-invoices-title">
              Open Invoices
            </Typography>
            <DistributeButton selectedInvoices={selectedInvoices} />
            <QuickAddAdjustmentButton onClick={handleOpenQuickAddAdjustments} selectedInvoices={selectedInvoices} />
          </Box>
          {selectedCustomerId ? (
            isLoadingFormData ? (
              <Loading localized localizedSx={{ height: '100px' }} />
            ) : (
              <CreateBulkInvoicePaymentTable
                openInvoices={openInvoices}
                onSelectionChange={handleInvoiceSelectionChange}
                selectedInvoices={selectedInvoices}
                data-testid="bulk-invoice-payment-table"
              />
            )
          ) : (
            <Typography variant="body2" align="center" mb={4} data-testid="select-consumer-message">
              Select a consumer to view their open invoices
            </Typography>
          )}
        </Box>
        <Box p={3} width="95%" data-testid="payment-note-section">
          <Typography variant="h6" gutterBottom data-testid="payment-note-title">
            Payment Note
          </Typography>
          <Box p={2} data-testid="payment-note-box">
            <Input
              name="paymentNote"
              label="Notes"
              defaultValue=""
              placeholder="Enter a note..."
              control={control}
              variant="outlined"
              fullWidth
              error={!!errors.paymentNote}
              helperText={errors.paymentNote?.message}
              data-testid="payment-note-input"
              disabled={!selectedCustomerId}
            />
          </Box>
        </Box>

        <QuickAddAdjustments
          open={isQuickAddAdjustmentsOpen}
          onClose={handleCloseQuickAddAdjustments}
          onAddAdjustments={onAddAdjustments}
          selectedInvoices={selectedInvoices}
          data-testid="quick-add-adjustments"
        />
      </form>
    </FormProvider>
  )
}

export default CreateBulkInvoicePaymentContainer
